import type { ReactNode } from 'react';
import { Card } from './Card';
import { Button } from './Button';

interface AlertModalProps {
  title: string;
  message: ReactNode;
  type?: 'info' | 'success' | 'warning' | 'error';
  onClose: () => void;
  confirmText?: string;
}

export function AlertModal({ title, message, type = 'info', onClose, confirmText = 'OK' }: AlertModalProps) {
  // Styles based on type
  const headerColors = {
    info: 'var(--text-1)',
    success: 'var(--success-main, #10b981)',
    warning: 'var(--warning-main, #f59e0b)',
    error: 'var(--danger-main, #ef4444)'
  };

  const IconWrapper = () => {
    switch (type) {
      case 'success':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: headerColors.success, marginBottom: '12px' }}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        );
      case 'error':
        return (
           <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: headerColors.error, marginBottom: '12px' }}>
            <circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        );
      case 'warning':
        return (
           <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: headerColors.warning, marginBottom: '12px' }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        );
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--brand-primary)', marginBottom: '12px' }}>
            <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        );
    }
  };

  return (
    <div 
      style={{ 
        position: 'fixed', 
        inset: 0, 
        background: 'rgba(0,0,0,.45)', 
        backdropFilter: 'blur(3px)', 
        zIndex: 2000, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '1rem', 
        animation: 'fadeIn .15s ease' 
      }} 
      onClick={e => { if (e.target === e.currentTarget) onClose() }} 
      role="dialog" 
      aria-modal="true"
    >
      <Card style={{ width: '100%', maxWidth: '400px', margin: 0, animation: 'slideUp .18s ease', overflow: 'hidden' }}>
        <Card.Body style={{ padding: '24px 24px 16px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <IconWrapper />
          <h3 style={{ fontSize: '1.1rem', color: 'var(--text-1)', marginBottom: '12px', fontWeight: 600 }}>{title}</h3>
          
          <div style={{ color: 'var(--text-2)', fontSize: '0.95rem', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
            {message}
          </div>
        </Card.Body>
        <div className="ui-actions" style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', background: 'var(--surface-2)', marginTop: 0, justifyContent: 'center' }}>
          <Button onClick={onClose} autoFocus style={{ minWidth: '120px' }}>{confirmText}</Button>
        </div>
      </Card>
    </div>
  );
}
